import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Alert, Badge, Table, Modal, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faEdit,
    faTrash,
    faCheck,
    faTimes,
    faEye,
    faCalculator,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

const SerialRangeInput = ({
    cardStockId,
    onRangeCreated,
    onRangeUpdated,
    existingRanges = []
}) => {
    const [formData, setFormData] = useState({
        startSerial: '',
        endSerial: '',
        serialFormat: 'NUMERIC',
        prefix: '',
        suffix: '',
        serialLength: 10,
        batchNumber: '',
        notes: ''
    });

    const [validation, setValidation] = useState({
        isValid: false,
        totalQuantity: 0,
        errors: [],
        warnings: []
    });

    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        validateRange();
    }, [formData.startSerial, formData.endSerial, formData.serialFormat]);

    const validateRange = () => {
        const errors = [];
        const warnings = [];
        let totalQuantity = 0;

        if (formData.startSerial && formData.endSerial) {
            if (formData.serialFormat === 'NUMERIC') {
                const start = parseInt(formData.startSerial);
                const end = parseInt(formData.endSerial);

                if (isNaN(start) || isNaN(end)) {
                    errors.push('Les numéros de série doivent être numériques');
                } else if (start >= end) {
                    errors.push('Le numéro de fin doit être supérieur au numéro de début');
                } else {
                    totalQuantity = end - start + 1;

                    // Vérifications supplémentaires
                    if (totalQuantity > 100000) {
                        warnings.push('Intervalle très large (>100k cartes). Vérifiez que c\'est intentionnel.');
                    }

                    // Vérifier les chevauchements avec les intervalles existants
                    const overlapping = existingRanges.find(range =>
                        (start >= parseInt(range.startSerial) && start <= parseInt(range.endSerial)) ||
                        (end >= parseInt(range.startSerial) && end <= parseInt(range.endSerial)) ||
                        (start <= parseInt(range.startSerial) && end >= parseInt(range.endSerial))
                    );

                    if (overlapping) {
                        errors.push(`Chevauchement détecté avec l'intervalle ${overlapping.startSerial}-${overlapping.endSerial}`);
                    }
                }
            } else {
                // Validation alphanumérique plus basique
                totalQuantity = 1; // Estimation
                warnings.push('Validation limitée pour les formats alphanumériques');
            }
        }

        setValidation({
            isValid: errors.length === 0 && totalQuantity > 0,
            totalQuantity,
            errors,
            warnings
        });
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const generatePreview = () => {
        if (!validation.isValid) return;

        const preview = [];
        const maxPreview = 10;

        if (formData.serialFormat === 'NUMERIC') {
            const start = parseInt(formData.startSerial);
            const end = parseInt(formData.endSerial);

            for (let i = 0; i < Math.min(maxPreview, validation.totalQuantity); i++) {
                const num = start + i;
                let serial = num.toString().padStart(formData.serialLength, '0');

                if (formData.prefix) serial = formData.prefix + serial;
                if (formData.suffix) serial = serial + formData.suffix;

                preview.push({
                    sequence: i + 1,
                    serial: serial,
                    position: `${num} (${i + 1}/${validation.totalQuantity})`
                });
            }

            if (validation.totalQuantity > maxPreview) {
                preview.push({
                    sequence: '...',
                    serial: '...',
                    position: `... (${validation.totalQuantity - maxPreview} autres)`
                });

                // Ajouter quelques exemples de la fin
                const endStart = end - 2;
                for (let i = Math.max(0, endStart - start); i < end - start + 1; i++) {
                    const num = start + i;
                    let serial = num.toString().padStart(formData.serialLength, '0');

                    if (formData.prefix) serial = formData.prefix + serial;
                    if (formData.suffix) serial = serial + formData.suffix;

                    preview.push({
                        sequence: i + 1,
                        serial: serial,
                        position: `${num} (${i + 1}/${validation.totalQuantity})`
                    });
                }
            }
        }

        setPreviewData(preview);
        setShowPreview(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validation.isValid) return;

        setLoading(true);

        try {
            const rangeData = {
                cardStockId: cardStockId,
                startSerial: formData.startSerial,
                endSerial: formData.endSerial,
                totalQuantity: validation.totalQuantity,
                serialFormat: formData.serialFormat,
                prefix: formData.prefix || null,
                suffix: formData.suffix || null,
                serialLength: formData.serialLength,
                batchNumber: formData.batchNumber || null,
                notes: formData.notes || null,
                createdBy: localStorage.getItem('userName') || 'Admin'
            };

            // Appel API réel via StockService
            const API_BASE_URL = process.env.REACT_APP_BASEURL_CLIENT_BACKEND;
            const response = await fetch(`${API_BASE_URL}/Stock/${cardStockId}/serial-ranges`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(rangeData)
            });

            if (!response.ok) {
                throw new Error(`Erreur API: ${response.status}`);
            }

            const createdRange = await response.json();
            console.log('Nouvelle plage de numéros créée:', createdRange);

            if (onRangeCreated) {
                onRangeCreated(createdRange);
            }

            // Réinitialiser le formulaire
            setFormData({
                startSerial: '',
                endSerial: '',
                serialFormat: 'NUMERIC',
                prefix: '',
                suffix: '',
                serialLength: 10,
                batchNumber: '',
                notes: ''
            });

        } catch (error) {
            console.error('Erreur création plage:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <Card.Header>
                <h5 className="mb-0">
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Créer une nouvelle plage de numéros de série
                </h5>
            </Card.Header>
            <Card.Body>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Format des numéros *</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={formData.serialFormat}
                                    onChange={(e) => handleInputChange('serialFormat', e.target.value)}
                                >
                                    <option value="NUMERIC">Numérique</option>
                                    <option value="ALPHANUMERIC">Alphanumérique</option>
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Numéro de début *</Form.Label>
                                <Form.Control
                                    type={formData.serialFormat === 'NUMERIC' ? 'number' : 'text'}
                                    value={formData.startSerial}
                                    onChange={(e) => handleInputChange('startSerial', e.target.value)}
                                    placeholder="1000000"
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Numéro de fin *</Form.Label>
                                <Form.Control
                                    type={formData.serialFormat === 'NUMERIC' ? 'number' : 'text'}
                                    value={formData.endSerial}
                                    onChange={(e) => handleInputChange('endSerial', e.target.value)}
                                    placeholder="1099999"
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Préfixe</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.prefix}
                                    onChange={(e) => handleInputChange('prefix', e.target.value)}
                                    placeholder="CARD"
                                    maxLength={10}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Suffixe</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.suffix}
                                    onChange={(e) => handleInputChange('suffix', e.target.value)}
                                    placeholder="FR"
                                    maxLength={10}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Longueur série</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={formData.serialLength}
                                    onChange={(e) => handleInputChange('serialLength', parseInt(e.target.value))}
                                    min={5}
                                    max={20}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Numéro de lot</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.batchNumber}
                                    onChange={(e) => handleInputChange('batchNumber', e.target.value)}
                                    placeholder="BATCH_2024_01"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Notes</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={formData.notes}
                                    onChange={(e) => handleInputChange('notes', e.target.value)}
                                    placeholder="Notes et informations additionnelles..."
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Validation et infos */}
                    {validation.totalQuantity > 0 && (
                        <Alert variant="info">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <FontAwesomeIcon icon={faCalculator} className="me-2" />
                                    <strong>Quantité totale : {validation.totalQuantity.toLocaleString()} cartes</strong>
                                </div>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={generatePreview}
                                    disabled={!validation.isValid}
                                >
                                    <FontAwesomeIcon icon={faEye} className="me-1" />
                                    Prévisualiser
                                </Button>
                            </div>
                        </Alert>
                    )}

                    {validation.errors.length > 0 && (
                        <Alert variant="danger">
                            <FontAwesomeIcon icon={faTimes} className="me-2" />
                            <strong>Erreurs :</strong>
                            <ul className="mb-0 mt-2">
                                {validation.errors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </Alert>
                    )}

                    {validation.warnings.length > 0 && (
                        <Alert variant="warning">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            <strong>Avertissements :</strong>
                            <ul className="mb-0 mt-2">
                                {validation.warnings.map((warning, index) => (
                                    <li key={index}>{warning}</li>
                                ))}
                            </ul>
                        </Alert>
                    )}

                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={!validation.isValid || loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Création...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                                    Créer la plage
                                </>
                            )}
                        </Button>
                    </div>
                </Form>

                {/* Modal de prévisualisation */}
                <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            Prévisualisation des numéros de série
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="mb-3">
                            <Badge bg="info" className="me-2">
                                Total : {validation.totalQuantity.toLocaleString()} cartes
                            </Badge>
                            <Badge bg="secondary">
                                Format : {formData.serialFormat}
                            </Badge>
                        </div>

                        <Table striped bordered hover size="sm">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Numéro de série</th>
                                    <th>Position</th>
                                </tr>
                            </thead>
                            <tbody>
                                {previewData.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.sequence}</td>
                                        <td>
                                            <code className="text-primary">{item.serial}</code>
                                        </td>
                                        <td className="text-muted small">{item.position}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowPreview(false)}>
                            Fermer
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Card.Body>
        </Card>
    );
};

export default SerialRangeInput;